/**
 * Bandeau « Parcellaire à renseigner » — rappel post-onboarding.
 *
 * Apparaît sur le dashboard quand :
 *   - L'onboarding est OFFICIELLEMENT terminé (`onboarding_termine_le` SET),
 *     donc le bandeau OnboardingBanner ne s'affiche plus.
 *   - Le vigneron a choisi "Plus tard" sur l'étape 4 (parcellaire) du wizard,
 *     donc `nb_parcelles === 0`.
 *
 * Pourquoi un bandeau dédié plutôt que le mini-lien dans la carte domaine :
 * un rappel structurant doit être visible, pas caché dans un coin. Sans
 * parcellaire, pas de Déclaration de Récolte au 10 décembre — c'est un
 * vrai blocage métier à venir.
 *
 * Style : ton champagne/gold (rappel doux), pas rust (qui est réservé aux
 * vraies alertes échéance critique — cf doctrine couleur Dashboard.tsx).
 *
 * À faire évoluer (V0.5/V1) : factoriser avec d'autres rappels
 * post-onboarding (équipe non invitée, abonnement non choisi, agrément CIVC
 * non renseigné, etc.) dans un composant générique `RappelsCompletion`.
 * Task #3 du backlog — à discuter avec le frangin.
 */

import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Sprout } from "lucide-react";
import { Link } from "react-router-dom";

import { useMe } from "../../auth/use-me.js";
import { trpc } from "../../lib/trpc.js";

export function VignobleBanner() {
  const { data } = useMe();

  // Stats parcellaire : même queryKey que la carte domaine pour partager le
  // cache TanStack Query (pas de double fetch). Invalidé par
  // NouvelleParcelle.tsx au moment de la création → le bandeau disparaît
  // automatiquement dès la 1ʳᵉ parcelle ajoutée.
  const { data: stats } = useQuery({
    queryKey: ["parcellaire.statsResume"],
    queryFn: () => trpc.parcellaire.statsResume.query(),
  });

  if (!data) return null;

  const activeMembership =
    data.domaines_accessibles.find((m) => m.domaine.id === data.domaine_id_actif) ??
    data.domaines_accessibles[0];
  if (!activeMembership) return null;

  const { domaine } = activeMembership;

  // Conditions strictes pour éviter de doublonner OnboardingBanner :
  //   - Domaine actif (sinon redirigé vers /compte-en-attente)
  //   - Onboarding fini (sinon OnboardingBanner prend la main, plus prioritaire)
  //   - Stats chargées ET nb_parcelles === 0 (vraiment vide, pas "loading")
  if (domaine.statut !== "actif") return null;
  if (!domaine.onboarding_termine_le) return null;
  if (!stats || stats.nb_parcelles > 0) return null;

  return (
    <div className="rounded-xl border border-champagne-400 bg-gradient-to-r from-champagne-100/80 to-champagne-50/60 p-5">
      <div className="flex flex-wrap items-center gap-4">
        <div className="rounded-lg bg-champagne-200 p-2.5 text-champagne-800">
          <Sprout size={20} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-champagne-900">Parcellaire à renseigner</p>
          <p className="mt-0.5 text-xs text-champagne-800">
            Ajoutez vos parcelles pour démarrer. Vous pourrez les modifier ou en ajouter d'autres à
            tout moment.
          </p>
        </div>
        <Link
          to="/vignoble/nouvelle"
          className="ui-btn-primary inline-flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-medium"
        >
          Configurer le parcellaire
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
